import type { MarlinStoreDb } from "../db";
import { SELECTED_CAMERA_KEY } from "../state";
import type { ScreenLayout, SelectionRecord } from "../../types";

export type DanglingReference = {
  table: string;
  key: string;
  field: "machineId" | "siteId";
  value: number;
};

export const findDanglingReferences = async (
  db: MarlinStoreDb,
): Promise<DanglingReference[]> => {
  const machines = await db.machines.toArray();
  const machineIds = new Set(machines.map((m) => m.machine_id));
  const siteIds = new Set(machines.map((m) => m.site_id));
  const found: DanglingReference[] = [];

  const check = (
    table: string,
    key: string,
    field: "machineId" | "siteId",
    value: number | null | undefined,
  ): void => {
    if (value == null) return;
    const known = field === "machineId" ? machineIds : siteIds;
    if (!known.has(value)) found.push({ table, key, field, value });
  };

  const checkLayout = (
    table: string,
    prefix: string,
    layout: ScreenLayout,
  ): void => {
    for (const grid of ["cameraGrid", "cageGrid"] as const)
      for (const [gridId, cell] of Object.entries(
        layout.screenData[grid] ?? {},
      )) {
        if (!cell) continue;
        check(
          table,
          `${prefix}:${grid}:${gridId}`,
          "machineId",
          cell.machineId,
        );
        if (cell.pin)
          check(
            table,
            `${prefix}:${grid}:${gridId}:pin`,
            "machineId",
            cell.pin.machineId,
          );
      }
    for (const siteId of layout.feedScreenData?.sites?.siteIds ?? [])
      check(table, `${prefix}:feedSites`, "siteId", siteId);
  };

  for (const row of await db.notifications.toArray()) {
    check("notifications", String(row.rowId), "machineId", row.machineId);
    check("notifications", String(row.rowId), "siteId", row.siteId);
  }
  for (const row of await db.progress.toArray())
    check(
      "progress",
      `${row.actionId}:${row.machineId}:${row.messageId}`,
      "machineId",
      row.machineId,
    );
  for (const row of await db.offsets.toArray())
    check(
      "offsets",
      `${row.machineId}:${row.topic}`,
      "machineId",
      row.machineId,
    );
  const selected = (await db.settings.get(SELECTED_CAMERA_KEY))?.value as
    | SelectionRecord
    | null
    | undefined;
  if (selected)
    check(
      "selection",
      String(selected.screenId),
      "machineId",
      selected.machineId,
    );
  for (const screen of await db.screens.toArray())
    checkLayout("screens", String(screen.id), screen);
  for (const template of await db.templates.toArray())
    for (const screen of template.screenLayouts)
      checkLayout("templates", `${template.id}:${screen.id}`, screen);
  for (const template of await db.screenTemplates.toArray())
    checkLayout("screenTemplates", template.id, template.screenLayout);
  for (const action of await db.quickActions.toArray()) {
    for (const siteId of action.sites ?? [])
      check("quickActions", `${action.id}:sites`, "siteId", siteId);
    (action.steps ?? []).forEach((step, ordinal) => {
      for (const target of step.targets ?? []) {
        check(
          "quickActions",
          `${action.id}:${ordinal}`,
          "machineId",
          target.machineId,
        );
        check(
          "quickActions",
          `${action.id}:${ordinal}`,
          "siteId",
          target.siteId,
        );
      }
    });
  }
  return found;
};
